import apiClient from '@/api/client';
import type {
  Account,
  Ad,
  Asset,
  AssetGroup,
  Audience,
  AuditSummary,
  BiddingStrategy,
  Budget,
  Campaign,
  ChangeHistory,
  ConversionAction,
  ConversionStats,
  Description,
  ExportDownload,
  ExportOptions,
  ExportProgress,
  Extension,
  GoogleAdsConnection,
  Headline,
  ImageAsset,
  Keyword,
  Label,
  LandingPage,
  LocationTarget,
  NegativeKeyword,
  Performance,
  QualitySignals,
  Recommendation,
  RecommendationImpact,
  SearchTerm,
  Shopping,
  VideoAsset,
} from '@/types/googleAds';

const BASE_PATH = '/google-ads';

// OAuth & Connection
export async function getConnectionStatus(): Promise<GoogleAdsConnection> {
  const response = await apiClient.get<GoogleAdsConnection>(`${BASE_PATH}/connection`);
  return response.data;
}

export async function initiateOAuth(): Promise<{ authUrl: string }> {
  const response = await apiClient.post<{ authUrl: string }>(`${BASE_PATH}/oauth/initiate`);
  return response.data;
}

export async function completeOAuth(code: string, state: string): Promise<GoogleAdsConnection> {
  const response = await apiClient.post<GoogleAdsConnection>(`${BASE_PATH}/oauth/callback`, {
    code,
    state,
  });
  return response.data;
}

export async function disconnectAccount(): Promise<void> {
  await apiClient.delete(`${BASE_PATH}/connection`);
}

export async function refreshConnection(): Promise<GoogleAdsConnection> {
  const response = await apiClient.post<GoogleAdsConnection>(`${BASE_PATH}/oauth/refresh`);
  return response.data;
}

// Account Data
export async function getAccount(customerId: string): Promise<Account> {
  const response = await apiClient.get<Account>(`${BASE_PATH}/accounts/${customerId}`);
  return response.data;
}

export async function listAccounts(): Promise<Account[]> {
  const response = await apiClient.get<Account[]>(`${BASE_PATH}/accounts`);
  return response.data;
}

// Campaigns
export async function getCampaigns(customerId: string, dateRange?: string): Promise<Campaign[]> {
  const response = await apiClient.get<Campaign[]>(`${BASE_PATH}/accounts/${customerId}/campaigns`, {
    params: { dateRange },
  });
  return response.data;
}

export async function getCampaign(customerId: string, campaignId: string): Promise<Campaign> {
  const response = await apiClient.get<Campaign>(
    `${BASE_PATH}/accounts/${customerId}/campaigns/${campaignId}`,
  );
  return response.data;
}

// Asset Groups
export async function getAssetGroups(customerId: string, campaignId?: string): Promise<AssetGroup[]> {
  const response = await apiClient.get<AssetGroup[]>(`${BASE_PATH}/accounts/${customerId}/asset-groups`, {
    params: { campaignId },
  });
  return response.data;
}

// Assets
export async function getAssets(customerId: string, type?: string): Promise<Asset[]> {
  const response = await apiClient.get<Asset[]>(`${BASE_PATH}/accounts/${customerId}/assets`, {
    params: { type },
  });
  return response.data;
}

// Ads
export async function getAds(customerId: string, campaignId?: string, adGroupId?: string): Promise<Ad[]> {
  const response = await apiClient.get<Ad[]>(`${BASE_PATH}/accounts/${customerId}/ads`, {
    params: { campaignId, adGroupId },
  });
  return response.data;
}

// Headlines
export async function getHeadlines(customerId: string): Promise<Headline[]> {
  const response = await apiClient.get<Headline[]>(`${BASE_PATH}/accounts/${customerId}/headlines`);
  return response.data;
}

// Descriptions
export async function getDescriptions(customerId: string): Promise<Description[]> {
  const response = await apiClient.get<Description[]>(`${BASE_PATH}/accounts/${customerId}/descriptions`);
  return response.data;
}

// Images
export async function getImages(customerId: string): Promise<ImageAsset[]> {
  const response = await apiClient.get<ImageAsset[]>(`${BASE_PATH}/accounts/${customerId}/images`);
  return response.data;
}

// Videos
export async function getVideos(customerId: string): Promise<VideoAsset[]> {
  const response = await apiClient.get<VideoAsset[]>(`${BASE_PATH}/accounts/${customerId}/videos`);
  return response.data;
}

// Keywords
export async function getKeywords(
  customerId: string,
  campaignId?: string,
  adGroupId?: string,
): Promise<Keyword[]> {
  const response = await apiClient.get<Keyword[]>(`${BASE_PATH}/accounts/${customerId}/keywords`, {
    params: { campaignId, adGroupId },
  });
  return response.data;
}

// Negative Keywords
export async function getNegativeKeywords(
  customerId: string,
  campaignId?: string,
): Promise<NegativeKeyword[]> {
  const response = await apiClient.get<NegativeKeyword[]>(
    `${BASE_PATH}/accounts/${customerId}/negative-keywords`,
    { params: { campaignId } },
  );
  return response.data;
}

// Search Terms
export async function getSearchTerms(
  customerId: string,
  dateRange?: string,
  campaignId?: string,
): Promise<SearchTerm[]> {
  const response = await apiClient.get<SearchTerm[]>(`${BASE_PATH}/accounts/${customerId}/search-terms`, {
    params: { dateRange, campaignId },
  });
  return response.data;
}

// Locations
export async function getLocationTargets(customerId: string, campaignId?: string): Promise<LocationTarget[]> {
  const response = await apiClient.get<LocationTarget[]>(`${BASE_PATH}/accounts/${customerId}/locations`, {
    params: { campaignId },
  });
  return response.data;
}

// Audiences
export async function getAudiences(customerId: string, campaignId?: string): Promise<Audience[]> {
  const response = await apiClient.get<Audience[]>(`${BASE_PATH}/accounts/${customerId}/audiences`, {
    params: { campaignId },
  });
  return response.data;
}

// Recommendations
export async function getRecommendations(customerId: string): Promise<Recommendation[]> {
  const response = await apiClient.get<Recommendation[]>(
    `${BASE_PATH}/accounts/${customerId}/recommendations`,
  );
  return response.data;
}

// Recommendation Impact
export async function getRecommendationImpact(customerId: string): Promise<RecommendationImpact[]> {
  const response = await apiClient.get<RecommendationImpact[]>(
    `${BASE_PATH}/accounts/${customerId}/recommendation-impact`,
  );
  return response.data;
}

// Conversion Actions
export async function getConversionActions(customerId: string): Promise<ConversionAction[]> {
  const response = await apiClient.get<ConversionAction[]>(
    `${BASE_PATH}/accounts/${customerId}/conversion-actions`,
  );
  return response.data;
}

// Conversion Stats
export async function getConversionStats(
  customerId: string,
  dateRange?: string,
): Promise<ConversionStats[]> {
  const response = await apiClient.get<ConversionStats[]>(
    `${BASE_PATH}/accounts/${customerId}/conversion-stats`,
    { params: { dateRange } },
  );
  return response.data;
}

// Budgets
export async function getBudgets(customerId: string): Promise<Budget[]> {
  const response = await apiClient.get<Budget[]>(`${BASE_PATH}/accounts/${customerId}/budgets`);
  return response.data;
}

// Bidding Strategies
export async function getBiddingStrategies(customerId: string): Promise<BiddingStrategy[]> {
  const response = await apiClient.get<BiddingStrategy[]>(
    `${BASE_PATH}/accounts/${customerId}/bidding-strategies`,
  );
  return response.data;
}

// Extensions
export async function getExtensions(customerId: string, type?: string): Promise<Extension[]> {
  const response = await apiClient.get<Extension[]>(`${BASE_PATH}/accounts/${customerId}/extensions`, {
    params: { type },
  });
  return response.data;
}

// Landing Pages
export async function getLandingPages(customerId: string): Promise<LandingPage[]> {
  const response = await apiClient.get<LandingPage[]>(`${BASE_PATH}/accounts/${customerId}/landing-pages`);
  return response.data;
}

// Performance
export async function getPerformance(customerId: string, dateRange: string): Promise<Performance> {
  const response = await apiClient.get<Performance>(`${BASE_PATH}/accounts/${customerId}/performance`, {
    params: { dateRange },
  });
  return response.data;
}

export async function getPerformanceComparison(
  customerId: string,
  dateRanges: string[],
): Promise<Performance[]> {
  const response = await apiClient.get<Performance[]>(
    `${BASE_PATH}/accounts/${customerId}/performance/compare`,
    { params: { dateRanges: dateRanges.join(',') } },
  );
  return response.data;
}

// Quality Signals
export async function getQualitySignals(customerId: string): Promise<QualitySignals[]> {
  const response = await apiClient.get<QualitySignals[]>(
    `${BASE_PATH}/accounts/${customerId}/quality-signals`,
  );
  return response.data;
}

// Labels
export async function getLabels(customerId: string): Promise<Label[]> {
  const response = await apiClient.get<Label[]>(`${BASE_PATH}/accounts/${customerId}/labels`);
  return response.data;
}

// Shopping
export async function getShoppingProducts(customerId: string): Promise<Shopping[]> {
  const response = await apiClient.get<Shopping[]>(`${BASE_PATH}/accounts/${customerId}/shopping`);
  return response.data;
}

// Change History
export async function getChangeHistory(
  customerId: string,
  months?: number,
  resourceType?: string,
): Promise<ChangeHistory[]> {
  const response = await apiClient.get<ChangeHistory[]>(
    `${BASE_PATH}/accounts/${customerId}/change-history`,
    { params: { months, resourceType } },
  );
  return response.data;
}

// Audit Summary
export async function getAuditSummary(customerId: string, dateRange?: string): Promise<AuditSummary> {
  const response = await apiClient.get<AuditSummary>(`${BASE_PATH}/accounts/${customerId}/audit-summary`, {
    params: { dateRange },
  });
  return response.data;
}

// Full Export
export async function startExport(options: ExportOptions): Promise<{ jobId: string }> {
  const response = await apiClient.post<{ jobId: string }>(`${BASE_PATH}/export`, options);
  return response.data;
}

export async function getExportProgress(jobId: string): Promise<ExportProgress> {
  const response = await apiClient.get<ExportProgress>(`${BASE_PATH}/export/${jobId}`);
  return response.data;
}

export async function cancelExport(jobId: string): Promise<void> {
  await apiClient.delete(`${BASE_PATH}/export/${jobId}`);
}

export async function downloadExport(jobId: string): Promise<ExportDownload> {
  const response = await apiClient.get<ExportDownload>(`${BASE_PATH}/export/${jobId}/download`);
  return response.data;
}

export async function downloadExportFile(jobId: string, fileName: string): Promise<Blob> {
  const response = await apiClient.get(`${BASE_PATH}/export/${jobId}/files/${fileName}`, {
    responseType: 'blob',
  });
  return response.data;
}

// Quick export (synchronous, for smaller accounts)
export async function quickExport(customerId: string, dateRange: string): Promise<ExportDownload> {
  const response = await apiClient.get<ExportDownload>(
    `${BASE_PATH}/accounts/${customerId}/quick-export`,
    { params: { dateRange } },
  );
  return response.data;
}
